/**
 * Slice 11 — fetch hook for the diagnostics dialog.
 *
 * Pulled out of `ConnectionDiagnosticsDialog.tsx` so the parent file stays
 * under the 200-line guideline once the Reconnect handling lands. Wraps the
 * `connection-diagnostics:run` IPC, the per-key cache, and the trigger
 * mapping (`'list_empty'` → `'list_failure'` for the API only).
 */

import { useEffect, useState } from 'react';
import type {
  DiagnosticsReport,
  DiagnosticsTrigger,
} from '../../../types/connectionDiagnostics';
import {
  readDiagnosticsCache,
  writeDiagnosticsCache,
  type CacheKeyArgs,
} from '../diagnosticsReportCache';
import { readLastListingAdapter } from '../utils/readLastListingAdapter';
import type { ShowConnectionDiagnosticsArgs } from '../showConnectionDiagnostics';

export type FetchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ready'; report: DiagnosticsReport }
  | { kind: 'error'; message: string };

function resolveApiTrigger(
  trigger: ShowConnectionDiagnosticsArgs['trigger'] | undefined
): DiagnosticsTrigger | undefined {
  if (trigger === undefined) return undefined;
  return trigger === 'list_empty' ? 'list_failure' : trigger;
}

export function useDiagnosticsFetch(args: {
  open: boolean;
  dialogArgs: ShowConnectionDiagnosticsArgs | null;
  /** Bumping this forces the hook to re-fetch (used by the Reconnect re-run). */
  refetchKey: number;
}): {
  fetchState: FetchState;
  setReport: (report: DiagnosticsReport) => void;
} {
  const { open, dialogArgs, refetchKey } = args;
  const [fetchState, setFetchState] = useState<FetchState>({ kind: 'idle' });
  const apiTrigger = resolveApiTrigger(dialogArgs?.trigger);

  useEffect(() => {
    if (!open || !dialogArgs || !apiTrigger) return;
    const cacheKey: CacheKeyArgs = {
      connectionId: dialogArgs.connectionId,
      trigger: dialogArgs.trigger,
      scopeDatabase: dialogArgs.scopeDatabase,
    };
    const cached = readDiagnosticsCache(cacheKey);
    if (cached) {
      setFetchState({ kind: 'ready', report: cached });
      return;
    }

    setFetchState({ kind: 'loading' });
    let cancelled = false;
    (async () => {
      try {
        if (!window.electronAPI?.runConnectionDiagnostics) {
          throw new Error('Diagnostics IPC unavailable');
        }
        const lastListingAdapter = readLastListingAdapter(dialogArgs.connectionId);
        const report = await window.electronAPI.runConnectionDiagnostics({
          connectionId: dialogArgs.connectionId,
          trigger: apiTrigger,
          scopeDatabase: dialogArgs.scopeDatabase,
          ...(lastListingAdapter ? { lastListingAdapter } : {}),
        });
        if (cancelled) return;
        writeDiagnosticsCache(cacheKey, report);
        setFetchState({ kind: 'ready', report });
      } catch (e) {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : 'Failed to run diagnostics';
        setFetchState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, dialogArgs, apiTrigger, refetchKey]);

  const setReport = (report: DiagnosticsReport) => {
    setFetchState({ kind: 'ready', report });
  };

  return { fetchState, setReport };
}
